import { useSelector } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Tooltip, Input, Button, Space, Typography, message, Spin } from 'antd';
import EbzModal from 'components/EbzModal';
import {
  InfoCircleOutlined,
  EyeTwoTone,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import ReCAPTCHA from 'react-google-recaptcha';
import merchantAPI from 'services/merchants';
import Alert from 'components/Alert';

function ReferralSignUpModal(props) {
  const {
    signUpModelDisplay,
    toggleModal,
    signupLoading,
    submitButtonDisable,
    setSignupLoading,
    setSubmitButtonDisable,
    fetchReferralsList,
    pagination
  } = props;

  //  variables
  const recaptchaRef = useRef();
  const [merchantSignUpForm] = Form.useForm();
  const refCode = useSelector((state) => state.user.currentUser?.ref_code);
  const [tooltipValidationOpen, setTooltipValidationOpen] = useState(false);
  const [validations, setValidations] = useState({
    upperCase: false,
    lowerCase: false,
    number: false,
    specialChar: false,
    length: false
  });

  const validatePassword = (value) => {
    if (value.trim().length === 0) {
      setTooltipValidationOpen(false);
    } else if (
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(value) &&
      value.length >= 8
    ) {
      setTooltipValidationOpen(false);
      merchantSignUpForm.setFieldValue('password', value);
    } else {
      setTooltipValidationOpen(true);
    }
    setValidations({
      upperCase: /[A-Z]/.test(value),
      lowerCase: /[a-z]/.test(value),
      number: /\d/.test(value),
      specialChar: /[!@#$%^&*]/.test(value),
      length: value.length >= 8
    });
  };

  const errorList = (errors) => {
    return Object.entries(errors).map(([key, value]) => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          margin: '8px 0'
        }}
      >
        <Typography.Title level={5} style={{ margin: 0 }}>
          {key} :
        </Typography.Title>{' '}
        &nbsp;
        <Typography.Text style={{ marginLeft: '8px' }}>{value}</Typography.Text>
      </div>
    ));
  };

  const sendSignUp = async (request) => {
    const token = await recaptchaRef.current.executeAsync();
    const response = await merchantAPI.createReferralSignup({ ...request, recaptcha_token: token });
    if (response.success) {
      toggleModal();
      fetchReferralsList(pagination);
      message.success('Signup successfully.');
    } else {
      merchantSignUpForm.resetFields();
      setSignupLoading(false);
      setSubmitButtonDisable(false);
      if (response?.message?.errors) {
        Alert('Oops', errorList(response?.message?.errors), 'error');
      } else {
        Alert('Oops', errorList(response?.message?.message), 'error');
      }
    }
    recaptchaRef.current.reset();
    merchantSignUpForm.resetFields();
  };
  const createSignUp = (payload) => {
    setSignupLoading(true);
    setSubmitButtonDisable(true);
    const request = {
      name: payload.brand_name,
      email: payload.business_email_id,
      mobile_number: payload.mobile_number,
      password: payload.password,
      ref_code: refCode,
      gstin_or_pan: payload.gst_business_pan
    };
    sendSignUp(request);
  };

  const handleDrawerClose = () => {
    toggleModal();
  };

  const renderIcon = (visible) => {
    if (visible) {
      return (
        <EyeTwoTone style={{ color: 'var(--color-primary)' }} twoToneColor="var(--color-primary)" />
      );
    }
    return <EyeInvisibleOutlined />;
  };

  useEffect(() => {
    if (refCode && signUpModelDisplay) {
      setSubmitButtonDisable(false);
      merchantSignUpForm.setFieldsValue({
        email: '',
        password: ''
      });
      merchantSignUpForm.setFieldValue('ref_code', refCode);
    }
  }, [refCode, signUpModelDisplay]);

  return (
    <EbzModal
      title={
        <Typography.Title
          style={{ fontSize: '18px', display: 'flex', justifyContent: 'center' }}
          level={4}
        >
          Signup
        </Typography.Title>
      }
      toggleModal={toggleModal}
      onClose={handleDrawerClose}
      width="700px"
      isOpen={signUpModelDisplay}
      scroll={{ y: '240px' }}
    >
      <Spin spinning={signupLoading}>
        <Form
          style={{ display: 'flex', justifyContent: 'center' }}
          form={merchantSignUpForm}
          name="new-sign-up"
          layout="vertical"
          onFinish={createSignUp}
        >
          <Row>
            <Col span={24}>
              <Form.Item
                name="brand_name"
                label={
                  <>
                    <Typography.Text style={{ fontSize: '16px' }}>Brand Name</Typography.Text>
                    <span className="required-mark" />
                    <Tooltip title="Doing Business As (DBA) Name">
                      <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                    </Tooltip>
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please enter Brand Name'
                  },
                  {
                    validator(_, value) {
                      if (
                        value !== undefined &&
                        value !== '' &&
                        !/^[a-zA-Z0-9.&-]*[a-zA-Z0-9][a-zA-Z0-9 .&-]*$/g.test(value)
                      ) {
                        // eslint-disable-next-line prefer-promise-reject-errors
                        return Promise.reject('Please enter a valid Brand Name.');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  style={{
                    width: '100%',
                    padding: '12px'
                  }}
                  placeholder="Enter brand name"
                  size="middle"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                style={{ marginBottom: '0px' }}
                name="business_email_id"
                label={
                  <>
                    <Typography.Text style={{ fontSize: '16px' }}>
                      Business Email ID
                    </Typography.Text>
                    <span className="required-mark" />
                    <Tooltip title="This email ID will be verified & will be used for further communication">
                      <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                    </Tooltip>
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please enter business email ID'
                  },
                  {
                    validator(_, value) {
                      if (
                        value !== undefined &&
                        value !== '' &&
                        !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/g.test(value)
                      ) {
                        // eslint-disable-next-line prefer-promise-reject-errors
                        return Promise.reject('Please enter a valid Business Email ID.');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  style={{
                    width: '100%',
                    padding: '12px'
                  }}
                  placeholder="Enter Business Email ID"
                  size="middle"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="mobile_number"
                label={
                  <>
                    <Typography.Text style={{ fontSize: '16px' }}>Mobile Number</Typography.Text>
                    <span className="required-mark" />
                    <Tooltip title="This mobile number will be used for further communication">
                      <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                    </Tooltip>
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please enter Mobile Number'
                  },
                  {
                    validator(_, value) {
                      if (
                        value !== undefined &&
                        value !== '' &&
                        !/^[6-9]\d{2}\d{3}\d{4}$/g.test(value)
                      ) {
                        // eslint-disable-next-line prefer-promise-reject-errors
                        return Promise.reject('Please enter a valid Mobile Number.');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  style={{
                    width: '100%',
                    padding: '12px'
                  }}
                  placeholder="Enter 10 digit mobile number"
                  size="middle"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="password"
                label={
                  <>
                    <Typography.Text style={{ fontSize: '16px' }}>Create Password</Typography.Text>
                    <span className="required-mark" />
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please enter password'
                  },
                  {
                    validator(_, value) {
                      if (value !== undefined && value !== '') {
                        // Check if password has at least one uppercase letter
                        if (!/(?=.*[A-Z])/.test(value)) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject(
                            'Password must contain at least one uppercase letter.'
                          );
                        }
                        // Check if password has at least one lowercase letter
                        if (!/(?=.*[a-z])/.test(value)) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject(
                            'Password must contain at least one lowercase letter.'
                          );
                        }
                        // Check if password has at least one digit
                        if (!/(?=.*\d)/.test(value)) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject('Password must contain at least one number.');
                        }
                        // Check if password has at least one special character
                        if (!/(?=.*[!@#$%^&*])/.test(value)) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject(
                            'Password must contain at least one special character.'
                          );
                        }
                        // Check if password is at least 8 characters long
                        if (!/^[A-Za-z\d!@#$%^&*]{8,}$/.test(value)) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject('Password must be at least 8 characters long.');
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Tooltip
                  placement="rightTop"
                  showArrow
                  open={tooltipValidationOpen}
                  title={
                    <div>
                      <Space direction="vertical">
                        <div>
                          {validations.upperCase ? (
                            <CheckCircleOutlined style={{ color: 'green' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: 'red' }} />
                          )}
                          <Typography.Text style={{ color: 'white' }}>
                            At least one uppercase letter
                          </Typography.Text>
                        </div>
                        <div>
                          {validations.lowerCase ? (
                            <CheckCircleOutlined style={{ color: 'green' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: 'red' }} />
                          )}
                          <Typography.Text style={{ color: 'white' }}>
                            {' '}
                            At least one lowercase letter
                          </Typography.Text>
                        </div>
                        <div>
                          {validations.number ? (
                            <CheckCircleOutlined style={{ color: 'green' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: 'red' }} />
                          )}
                          <Typography.Text style={{ color: 'white' }}>
                            At least one number
                          </Typography.Text>
                        </div>
                        <div>
                          {validations.specialChar ? (
                            <CheckCircleOutlined style={{ color: 'green' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: 'red' }} />
                          )}
                          <Typography.Text style={{ color: 'white' }}>
                            At least one special character
                          </Typography.Text>
                        </div>
                        <div>
                          {validations.length ? (
                            <CheckCircleOutlined style={{ color: 'green' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: 'red' }} />
                          )}
                          <Typography.Text style={{ color: 'white' }}>
                            At least 8 characters
                          </Typography.Text>
                        </div>
                      </Space>
                    </div>
                  }
                >
                  <Input.Password
                    style={{
                      width: '100%',
                      padding: '12px'
                    }}
                    placeholder="Enter password"
                    size="middle"
                    onChange={(e) => validatePassword(e.target.value)}
                    iconRender={(visible) => renderIcon(visible)}
                  />
                </Tooltip>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="gst_business_pan"
                label={
                  <>
                    <Typography.Text style={{ fontSize: '16px' }}>
                      GSTIN or Business PAN
                    </Typography.Text>
                    <span className="required-mark" />
                    <Tooltip title="In case of proprietorship & individual entity, upload personal PAN">
                      <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                    </Tooltip>
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please enter gst or pan'
                  },
                  {
                    validator(_, value) {
                      if (value !== undefined && value !== '') {
                        if (
                          /^\d{2}[A-Z]{4,5}\d{4,5}[A-Z]{1}[A-Z\d]{1}[ZDC]{1}[A-Z\d]{1}$/g.test(
                            value
                          )
                        ) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.resolve();
                        }
                        if (/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/g.test(value)) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.resolve();
                        }
                        // eslint-disable-next-line prefer-promise-reject-errors
                        return Promise.reject('Please enter a valid GSTIN or Business PAN Number.');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  style={{
                    width: '100%',
                    padding: '12px'
                  }}
                  placeholder="Enter GSTIN or PAN"
                  size="middle"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="ref_code"
                label={
                  <>
                    <Typography.Text style={{ fontSize: '16px' }}>Referral Code</Typography.Text>
                    <Tooltip title="In case of proprietorship & individual entity, upload personal PAN">
                      <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                    </Tooltip>
                  </>
                }
              >
                <Input
                  style={{
                    width: '100%',
                    padding: '12px'
                  }}
                  disabled
                  placeholder="Refferal Code"
                  size="middle"
                />
              </Form.Item>
            </Col>
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            />
            <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <Form.Item>
                <Button
                  htmlType="submit"
                  key="manual"
                  type="primary"
                  disabled={submitButtonDisable}
                >
                  Submit
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </EbzModal>
  );
}
ReferralSignUpModal.propTypes = {
  signUpModelDisplay: PropTypes.bool.isRequired,
  signupLoading: PropTypes.bool.isRequired,
  submitButtonDisable: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  setSubmitButtonDisable: PropTypes.func.isRequired,
  setSignupLoading: PropTypes.func.isRequired,
  fetchReferralsList: PropTypes.func.isRequired,
  pagination: PropTypes.instanceOf(Object).isRequired
};

export default ReferralSignUpModal;
